'use memo';

import { ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { Breakpoints, useWindowSize } from '@urbanx/agx-ui-components';
import slugify from 'slugify';
import clsx from 'clsx';
import {
  Menu,
  MenuTitles,
  StaffMenu,
  BlankContractMenu,
  ContractsMenu,
  Pages,
  filterSideMenuByRoles,
} from 'constants/menu';
import { SideMenu } from 'types/commonTypes';
import { AuthContext, StaffRole } from '@urbanx/agx.auth.client';
import { AgxRow, AgxSpacer } from '@urbanx/agx.ui-components2';

import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { useDoesUserHasRequiredRoles } from 'helpers/doesUserHaveRequiredRoles';
import './ContentLayout.scss';
import { CampaignDetailModelState } from '@urbanx/agx.campaigns.client';
import CampaignSectionHeader from 'components/page-header/CampaignSectionHeader';
import CampaignTabHeader from 'components/campaign-tab-header/CampaignTabHeader';
import CampaignStickyNotes from 'components/sticky-notes/CampaignStickyNotes';
import { useContentScroll } from 'contexts';

export interface ContentLayoutProps {
  children: ReactNode;
  isStaff?: boolean;
  isCampaigns?: boolean;
  isContracts?: boolean;
  secondaryActiveMenu?: MenuTitles;
  tertiaryActiveMenu?: MenuTitles;
  hasSecondaryMenu?: boolean;
  secondaryMenuOverride?: SideMenu[];
  tertiaryMenu?: SideMenu[];
  tertiaryTitle?: string;
  tertiaryActions?: ReactElement;
  secondaryActions?: ReactElement;
  hideBackground?: boolean;
  disableContainerRoundedCorners?: boolean;
}

const campaignTopMenuEndingPaths = ['/activity', '/files', '/contracts'] as const;
const contractTopMenuEndingPaths = ['/'] as const;

const CSS_CLASSES = {
  secondaryMenuContainer: 'secondaryMenuContainer',
  menuActionContainer: 'menuActionContainer',
} as const;

const ContentLayout = (props: ContentLayoutProps) => {
  const {
    hasSecondaryMenu,
    secondaryMenuOverride,
    tertiaryMenu,
    tertiaryTitle,
    tertiaryActions,
    secondaryActions,
    secondaryActiveMenu,
    tertiaryActiveMenu,
    isStaff,
    isCampaigns,
    isContracts,
    children,
    hideBackground,
    disableContainerRoundedCorners,
  } = props;
  const { agencyId, campaignId, contractId } = useParams();

  const { agency: selectedAgency, isLoading, isFetched } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const { setContentScrollPosition } = useContentScroll();
  const hasEffectiveHeader = isCampaigns || isContracts;

  // The sticky tab bar and sub-headers are now driven entirely by CSS
  // `position: sticky`; the scroll handler only reports the scroll offset used
  // by PageLayout to collapse the page header.
  const handleContentScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      setContentScrollPosition(e.currentTarget.scrollTop);
    },
    [setContentScrollPosition]
  );
  const location = useLocation();
  const { userAccount } = useContext(AuthContext);
  const windowSize = useWindowSize();
  const doesUserHaveRequiredRoles = useDoesUserHasRequiredRoles();
  const userRoles = useMemo(() => (userAccount?.roles as StaffRole[]) ?? [], [userAccount?.roles]);
  const effectiveHeader = useMemo(
    () => (hasEffectiveHeader ? <CampaignSectionHeader /> : undefined),
    [hasEffectiveHeader]
  );

  const blankContractMenu = useMemo(() => {
    if (!userAccount) return [];
    return filterSideMenuByRoles(BlankContractMenu, userRoles);
  }, [userAccount, userRoles]);

  const contractsMenu = useMemo(() => {
    return filterSideMenuByRoles(ContractsMenu, userRoles);
  }, [userRoles]);

  const campaignMenuItems = useMemo(
    () => secondaryMenuOverride ?? blankContractMenu,
    [secondaryMenuOverride, blankContractMenu]
  );

  const showSideMenu = useMemo(() => {
    const cleanPath = location.pathname.replace(/\/$/, '');
    const endingPaths = isCampaigns
      ? campaignTopMenuEndingPaths
      : isContracts
        ? contractTopMenuEndingPaths
        : null;

    if (!endingPaths) return false;
    return !endingPaths.some((end) => cleanPath.endsWith(end));
  }, [location.pathname, isCampaigns, isContracts]);

  const generateLink = useCallback(
    (pageLink: string) => {
      if (pageLink === '') return '/';

      if (agencyId) {
        if (contractId) {
          return `/${Pages.AGENCIES}/${agencyId}/campaigns/${campaignId}/contracts/${contractId}/${pageLink}`;
        } else if (campaignId) {
          return `/${Pages.AGENCIES}/${agencyId}/campaigns/${campaignId}/${pageLink}`;
        }

        return `/${Pages.AGENCIES}/${agencyId}/${pageLink}`;
      }
      return pageLink || '#';
    },
    [agencyId, campaignId, contractId]
  );

  const agencyStates = useMemo(() => {
    return (
      selectedAgency?.companyDetails?.licences
        ?.filter((licence) => !!licence?.state)
        ?.map((licence) => licence.state!) ?? []
    );
  }, [selectedAgency]);

  const isDesktop = useMemo(() => windowSize === Breakpoints.Desktop, [windowSize]);

  const tertiaryMenuContainerClassName = useMemo(() => {
    const shouldShow = (isDesktop && tertiaryActiveMenu) || (!isDesktop && !tertiaryActiveMenu);

    return clsx({
      tertiaryMenuContainer: isDesktop && tertiaryActiveMenu,
      tertiaryMenuContainerNoActiveMenu: !isDesktop && !tertiaryActiveMenu,
      hideContent: !shouldShow,
    });
  }, [isDesktop, tertiaryActiveMenu]);

  const filterMenuItems = useCallback(
    (menuItems: SideMenu[]) => {
      return menuItems.filter(
        (menu) =>
          !menu.isHidden &&
          doesUserHaveRequiredRoles(menu.requiredRoles) &&
          (menu.includeOnlyInState.length === 0 ||
            menu.includeOnlyInState.some((state) => agencyStates.includes(state)))
      );
    },
    [doesUserHaveRequiredRoles, agencyStates]
  );

  const renderSideMenu = useCallback(
    (
      menuItems: SideMenu[],
      activeMenu?: MenuTitles,
      isTertiary: boolean = false,
      insidePanel?: boolean
    ) => {
      const filteredMenuItems = filterMenuItems(menuItems);

      return filteredMenuItems.map((menu) => {
        const isActive = activeMenu === menu.title;
        const menuItemClassName = clsx({
          tertiaryMenuItem: isTertiary && tertiaryActiveMenu,
          tertiaryMenuItemNoActiveMenu: isTertiary && !tertiaryActiveMenu,
          secondaryMenuItemInPanel: !isTertiary && insidePanel,
          secondaryMenuItem: !isTertiary && !insidePanel,
          active: isActive,
        });

        const menuPageLink =
          isCampaigns &&
          menu.pageLink === Pages.PROPERTY_INFORMATION &&
          campaign?.state === CampaignDetailModelState.NSW
            ? Pages.PROPERTY_INFORMATION_SECTION
            : menu.pageLink;
        const linkPath = generateLink(menuPageLink ?? '');

        const menuKey = menu.pageLink ? `${menu.pageLink}-${menu.title}` : menu.title;

        return (
          <li key={menuKey} className={menuItemClassName}>
            <Link data-testid={`link-${slugify(menu.title)}`} to={linkPath}>
              {menu.title}
            </Link>
          </li>
        );
      });
    },
    [campaign?.state, filterMenuItems, generateLink, isCampaigns, tertiaryActiveMenu]
  );

  // Memoize className computations for better performance
  const mainContainerClassName = useMemo(
    () =>
      clsx('contentScrollContainer', {
        contentWithSecondaryMenu: hasSecondaryMenu,
        contentWithoutSecondaryMenu: !hasSecondaryMenu,
        containerBottomRoundedCorners: effectiveHeader,
        containerRoundedCorners: !effectiveHeader && !disableContainerRoundedCorners,
      }),
    [hasSecondaryMenu, effectiveHeader, disableContainerRoundedCorners]
  );

  const contentContainerClassName = useMemo(
    () =>
      clsx({
        contentWithTertiaryMenu: !!tertiaryMenu,
        contentWithoutTertiaryMenu: !tertiaryMenu,
        contentContainer: !!tertiaryMenu,
      }),
    [tertiaryMenu]
  );

  const innerContentContainerClassName = useMemo(
    () =>
      clsx({
        contentContainerWithTertiaryMenu: !!tertiaryMenu,
        contentContainer: !tertiaryMenu,
        // Sub-page header / side-nav stickiness is CSS-only and applies
        // whenever there is no tertiary menu (the offsets assume the campaign
        // tab bar is present).
        contentContainerStickyHeader: !tertiaryMenu,
        hideBackground,
      }),
    [tertiaryMenu, hideBackground]
  );

  const contentWithSideMenuClassName = useMemo(
    () =>
      clsx({
        contentWithSideMenuInPanel: isCampaigns || isContracts,
      }),
    [isCampaigns, isContracts]
  );

  const sideMenuContainerClassName = useMemo(
    () =>
      clsx({
        sideMenuContainer: isCampaigns || isContracts,
      }),
    [isCampaigns, isContracts]
  );

  const shouldShowTertiaryContent = useMemo(
    () => !(tertiaryMenu && !tertiaryActiveMenu),
    [tertiaryMenu, tertiaryActiveMenu]
  );

  // Helper to get the menu items for secondary menu based on context
  const getSecondaryMenuItems = useCallback(() => {
    if (isStaff) return StaffMenu;
    if (isCampaigns) return campaignMenuItems;
    if (isContracts) return contractsMenu;
    return Menu;
  }, [isStaff, isCampaigns, isContracts, campaignMenuItems, contractsMenu]);

  if (agencyId && (!isFetched || isLoading)) return null;

  return (
    <div className={mainContainerClassName} onScroll={handleContentScroll}>
      {effectiveHeader}
      <AgxSpacer className="stickySecondaryMenu" gap="spacing-md">
        {isDesktop && hasSecondaryMenu && (
          <ul className={CSS_CLASSES.secondaryMenuContainer}>
            {renderSideMenu(getSecondaryMenuItems(), secondaryActiveMenu)}
          </ul>
        )}
        <div className={CSS_CLASSES.menuActionContainer}>{secondaryActions}</div>
      </AgxSpacer>

      <AgxSpacer fill gap="spacing-md">
        {!isDesktop && hasSecondaryMenu && (
          <ul className={CSS_CLASSES.secondaryMenuContainer}>
            {renderSideMenu(getSecondaryMenuItems(), secondaryActiveMenu)}
          </ul>
        )}
        <div className={contentContainerClassName}>
          <div className={contentWithSideMenuClassName}>
            {isCampaigns && tertiaryMenu && (
              <ul className={CSS_CLASSES.secondaryMenuContainer}>
                {renderSideMenu(campaignMenuItems, secondaryActiveMenu, false, true)}
              </ul>
            )}
            {isContracts && tertiaryMenu && (
              <ul className={CSS_CLASSES.secondaryMenuContainer}>
                {renderSideMenu(contractsMenu, secondaryActiveMenu, false, true)}
              </ul>
            )}
          </div>
          <div
            className={
              showSideMenu && tertiaryMenu
                ? 'rightContentContainerWithSideMenu'
                : 'rightContentContainerWithoutSideMenu'
            }
          >
            <AgxSpacer fill gap="spacing-3xl">
              {(tertiaryTitle || tertiaryActions) && (
                <CampaignTabHeader
                  title={tertiaryTitle}
                  titleId="tertiaryTitleText"
                  actions={tertiaryActions}
                  variant="tertiary"
                />
              )}
              <AgxRow fill gap="spacing-xl">
                {tertiaryMenu && (
                  <ul className={tertiaryMenuContainerClassName}>
                    {renderSideMenu(tertiaryMenu, tertiaryActiveMenu, true)}
                  </ul>
                )}

                {shouldShowTertiaryContent && (
                  <div className={innerContentContainerClassName}>
                    <div className={contentWithSideMenuClassName}>
                      <div className={showSideMenu ? sideMenuContainerClassName : ''}>
                        {isCampaigns && showSideMenu && !tertiaryMenu && (
                          <ul className={CSS_CLASSES.secondaryMenuContainer}>
                            {renderSideMenu(campaignMenuItems, secondaryActiveMenu, false, true)}
                          </ul>
                        )}
                        {isContracts && showSideMenu && !tertiaryMenu && (
                          <ul className={CSS_CLASSES.secondaryMenuContainer}>
                            {renderSideMenu(contractsMenu, secondaryActiveMenu, false, true)}
                          </ul>
                        )}
                      </div>
                      <div
                        className={
                          showSideMenu && !tertiaryMenu
                            ? 'rightContentContainerWithSideMenu'
                            : 'rightContentContainerWithoutSideMenu'
                        }
                      >
                        {children}
                      </div>
                    </div>
                  </div>
                )}
              </AgxRow>
            </AgxSpacer>
          </div>
        </div>
      </AgxSpacer>
      {campaignId && <CampaignStickyNotes key={campaignId} campaignId={campaignId} />}
    </div>
  );
};

export default ContentLayout;
